import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Tooltip,
  message
} from 'antd';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import { GENDERS } from 'constants/users';
import { useOrganizationsStateContext } from 'providers/Organizations';
import {
  addAdminToOrganization,
  removeAdminFromOrganization
} from 'services/api/organizations';
import { updateUser } from 'services/api/users';
import { UserListRow } from 'types/api/users';

import { showErrorMessage } from 'utils/showErrorMessage';
import DetachOrganization from './DetachOrganization';

interface IProps {
  onClose(): void;
  refetch(): void;
  admin: UserListRow;
}

/** One membership, as the admin row carries it. */
type Organization = { id: number; name: Record<string, string> };

/** The picker value that stages every organization still available. */
const ALL_ORGANIZATIONS = 'all';

/**
 * Edit an admin's own details.
 *
 * EVERYTHING WAITS FOR SAVE, including the organizations.
 *
 * They are separate endpoints — the profile is one PUT, each attach is a
 * POST and each detach a DELETE that may carry a successor — so it would
 * have been easier to fire each one as it was clicked. It would also have
 * meant a dialog where Cancel undoes some of what you did and not the
 * rest, which is the kind of control people stop trusting. The section
 * below stages instead: the tags show what the admin WILL be in, and
 * nothing is written until Save.
 *
 * ATTACHES RUN BEFORE DETACHES, and the order is load-bearing. The API
 * refuses to remove an admin's last organization, so moving someone from
 * one to another — most admins, since 35 of 54 on dev hold exactly one —
 * fails at the first step if the detach goes first. The earlier order was
 * chosen so nobody could be seen in both at once; this one is about what
 * the data may be rather than how it reads, and belonging to two
 * organizations for a moment is harmless where belonging to none is the
 * state being prevented. The failure mode is better too: a detach that
 * fails leaves them in both, which is visible and retryable, rather than
 * in neither.
 *
 * Each call is marked done as it succeeds, so a failure half way leaves
 * the dialog open with only what is still outstanding — pressing Save
 * again finishes the job rather than repeating it.
 *
 * WHAT IS STILL NOT HERE:
 *
 *   DATE OF BIRTH. Create-only — UpdateUserDto has no field for it and
 *   forbidNonWhitelisted turns an unknown key into a 400. Gender used to
 *   be in this list and is not any more.
 *
 *   PASSWORD. The DTO accepts it, but Change password is its own action on
 *   the same menu and goes through the superadmin-only endpoint. Two ways
 *   to set a password, with different permissions, is worse than one.
 *
 * Who may open this is decided by the row's `canAct` on the admins list —
 * the server's strictly-below answer — so this component does not re-test
 * it. The attach and detach routes now enforce the same rule themselves,
 * plus "an organization you are in", which is why the picker below offers
 * only the operator's own organizations.
 */
const EditAdminModal: FC<IProps> = ({ admin, onClose, refetch }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // What the admin WILL belong to once this is saved — the memberships the
  // row arrived with, plus the staged changes, because that is what the
  // operator needs to see while deciding.
  const [organizations, setOrganizations] = useState(admin.organizations ?? []);
  const [detaching, setDetaching] = useState<Organization | null>(null);
  // Controlled and always cleared: the picker is an action, not a field,
  // so it shows its placeholder again the moment a choice is made.
  const [picked, setPicked] = useState<string | undefined>(undefined);

  /**
   * The staged work, as two lists of calls still to make.
   *
   * Held against the memberships the row ARRIVED with, not against the
   * tags on screen: adding an organization and then removing it again
   * before saving is no work at all, and must not become a POST followed
   * by a DELETE — which, for a detach, would be a DELETE asking who
   * inherits accounts that were never there.
   */
  const [pendingAttach, setPendingAttach] = useState<number[]>([]);
  const [pendingDetach, setPendingDetach] = useState<
    Array<{ id: number; reassignTo?: number }>
  >([]);

  const held = useMemo(
    () => new Set((admin.organizations ?? []).map(org => org.id)),
    [admin.organizations]
  );

  /**
   * AN ADMIN MUST BELONG TO AT LEAST ONE ORGANIZATION.
   *
   * Not a style rule — it is what makes the account usable at all. Every
   * admin request is scoped to the organizations the caller holds, and the
   * backend resolves "holds none" to "sees none" on purpose, because the
   * other reading would silently hand a new account every country. So an
   * admin with no organization signs in successfully, keeps their role and
   * permissions, and finds every screen empty, with nothing anywhere
   * explaining why.
   *
   * Nothing could produce that account until this screen could detach the
   * last one. So the last one cannot be detached: the tag stops offering
   * its X, and the dialog that asks about handing work over is never
   * reached. Moving an admin between organizations still works — add the
   * new one first, then remove the old.
   */
  const isLastOrganization = organizations.length === 1;
  const hasNoOrganization = !organizations.length;

  /**
   * ALL AS A STANDING GRANT, not as "happens to be all of them today".
   *
   * The server sets this only when a superadmin pressed All, and it keeps
   * granting each new organization to those admins — so the panel can say
   * "All" and mean it, rather than listing nineteen tags that would
   * quietly become nineteen of twenty.
   *
   * Shown as one chip, with the list a click away, because the only way to
   * change it is still per organization: detaching one clears the flag on
   * the server, which is correct — an admin missing one is a list, not
   * "all" — and the operator has to be able to pick WHICH one.
   */
  const [isListExpanded, setListExpanded] = useState(false);
  const showAllChip = !!admin.allOrganizations && !isListExpanded;

  // The operator's OWN organizations — the picker for the header, which is
  // scoped to their memberships. That is the same bound the attach route
  // applies, so offering anything else would just be offering a 403.
  const { results: ownOrganizations } = useOrganizationsStateContext();

  const attachable = useMemo(() => {
    const shown = new Set(organizations.map(org => org.id));

    return ownOrganizations.filter(
      item => item.value !== ALL_ORGANIZATIONS && !shown.has(Number(item.value))
    );
  }, [organizations, ownOrganizations]);

  /**
   * ALL means "every one still on this list", staged exactly as if each
   * had been picked by hand — which is what it is, since there is no bulk
   * attach endpoint and each membership is its own write on save.
   *
   * Offered only when it would do more than the single option beneath it.
   * It is the same snapshot the create form's ALL is: what the operator
   * holds today, not a standing rule about organizations added later.
   */
  const attachOptions = useMemo(
    () =>
      attachable.length > 1
        ? [
            { label: 'All organizations', value: ALL_ORGANIZATIONS },
            ...attachable
          ]
        : attachable,
    [attachable]
  );

  const closeModal = () => {
    onClose();
    form.resetFields();
  };

  const onAttach = useCallback(
    (value: string) => {
      const picks =
        value === ALL_ORGANIZATIONS
          ? attachable
          : attachable.filter(item => item.value === value);

      setPicked(undefined);
      setOrganizations(current => [
        ...current,
        ...picks.map(item => ({
          id: Number(item.value),
          name: { en: item.label ?? '' }
        }))
      ]);

      picks.forEach(item => {
        const id = Number(item.value);

        // Re-adding one that is only staged for removal cancels that
        // removal rather than queueing both halves.
        if (held.has(id)) {
          setPendingDetach(current => current.filter(row => row.id !== id));

          return;
        }

        setPendingAttach(current => [...current, id]);
      });
    },
    [attachable, held]
  );

  /**
   * Stage a removal. An organization that is only staged for attachment
   * has nothing on the server yet, so it is simply dropped — no
   * confirmation, and no question about who inherits what.
   */
  const onRemoveTag = useCallback(
    (org: Organization) => {
      if (!held.has(org.id)) {
        setOrganizations(current => current.filter(item => item.id !== org.id));
        setPendingAttach(current => current.filter(id => id !== org.id));

        return;
      }

      setDetaching(org);
    },
    [held]
  );

  const onFinish = useCallback(
    async (values: {
      first_name: string;
      last_name: string;
      email: string;
      phone?: string;
      gender?: string;
    }) => {
      try {
        setLoading(true);

        // The DTO is camelCase. /users RESPONSES come back snake_case
        // through toSnake — which is why the fields are named that way,
        // matching the row they were filled from — but the request body is
        // not renamed, and an unknown key is a 400 rather than a silent
        // drop.
        await updateUser(admin.id, 'admin', {
          firstName: values.first_name,
          lastName: values.last_name,
          email: values.email,
          phone: values.phone,
          gender: values.gender
        });

        // Sequential, and each one dropped from the queue as it lands. A
        // failure in the middle then leaves the dialog open showing only
        // what has not happened yet, instead of replaying the calls that
        // already succeeded.
        // ATTACHES FIRST — see the note on the component. The API will not
        // take away a last organization, so a move must add before it
        // removes or it is refused at the first step.
        for (const id of pendingAttach) {
          await addAdminToOrganization(id, admin.id);
          setPendingAttach(current =>
            current.filter(pending => pending !== id)
          );
        }

        for (const item of pendingDetach) {
          await removeAdminFromOrganization(item.id, admin.id, item.reassignTo);
          setPendingDetach(current =>
            current.filter(pending => pending.id !== item.id)
          );
        }

        message.success('Successfully updated');
        refetch();
        closeModal();
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [admin.id, pendingAttach, pendingDetach, refetch]
  );

  return (
    <Modal
      open
      title="Edit admin"
      onCancel={closeModal}
      width={600}
      onOk={form.submit}
      okText="Save"
      // Blocked rather than hidden: an account already in this state — one
      // that predates the rule — has to be fixable from here, and the only
      // fix is adding an organization.
      okButtonProps={{ disabled: hasNoOrganization }}
      confirmLoading={loading}
      destroyOnClose
    >
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        size="large"
        initialValues={{
          first_name: admin.first_name,
          last_name: admin.last_name,
          email: admin.email,
          phone: admin.phone ?? undefined,
          gender: admin.gender ?? undefined
        }}
      >
        <Form.Item
          name="first_name"
          label="First name"
          rules={[
            { required: true },
            { max: 25, min: 3 },
            {
              message: 'Only latin letters are allowed',
              pattern: /^[A-Za-z][A-Za-z\s-]*$/
            }
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="last_name"
          label="Last name"
          rules={[
            { required: true },
            { max: 25, min: 3 },
            {
              message: 'Only latin letters are allowed',
              pattern: /^[A-Za-z][A-Za-z\s-]*$/
            }
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[{ required: true, type: 'email' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="phone" label="Phone number">
          <Input />
        </Form.Item>
        <Form.Item name="gender" label="Gender">
          <Select allowClear options={GENDERS} />
        </Form.Item>
      </Form>
      {/*
        Outside the Form because these are not fields — they are calls
        queued for Save, and antd's Form would want them to be values.
      */}
      <div style={{ marginTop: 8 }}>
        <div style={{ fontWeight: 500 }}>
          Organizations <span style={{ color: 'var(--danger-color)' }}>*</span>
        </div>
        <div style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 8 }}>
          Applied when you save, with the rest of the changes. An admin must be
          in at least one.
        </div>
        {showAllChip ? (
          <div style={{ marginBottom: 12 }}>
            <Tag color="blue">All organizations</Tag>
            <Button
              type="link"
              size="small"
              style={{ paddingInline: 4 }}
              onClick={() => setListExpanded(true)}
            >
              {`Show the ${organizations.length}`}
            </Button>
            <div style={{ color: 'rgba(0,0,0,0.45)' }}>
              Organizations created from now on are added for them too. Removing
              one ends that.
            </div>
          </div>
        ) : (
          <Space size={[0, 8]} wrap style={{ marginBottom: 12 }}>
            {organizations.length ? (
              organizations.map(org =>
                isLastOrganization ? (
                  <Tooltip
                    key={org.id}
                    title="An admin must be in at least one organization. Add another before removing this one."
                  >
                    <Tag>{org.name?.en || org.id}</Tag>
                  </Tooltip>
                ) : (
                  <Tag
                    key={org.id}
                    closable
                    onClose={event => {
                      // Never let the tag close itself: removing one they
                      // already belong to depends on what they own inside
                      // that organization, and that takes a question first.
                      event.preventDefault();
                      onRemoveTag(org);
                    }}
                  >
                    {org.name?.en || org.id}
                  </Tag>
                )
              )
            ) : (
              <span style={{ color: 'var(--danger-color)' }}>
                This admin is in no organization, so every screen is empty for
                them. Add one to save.
              </span>
            )}
          </Space>
        )}
        {!showAllChip && !!attachable.length && (
          <Select
            // Remounted on every attach. A single-mode Select keeps the
            // chosen label as its search text until it loses focus, so a
            // cleared value alone still reads as though the organization
            // were selected rather than already added.
            key={`attach-${organizations.length}`}
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
            placeholder="Add to an organization"
            value={picked}
            options={attachOptions}
            onSelect={onAttach}
          />
        )}
      </div>
      {detaching && (
        <DetachOrganization
          admin={admin}
          organization={detaching}
          endsAllOrganizations={!!admin.allOrganizations}
          onClose={() => setDetaching(null)}
          onConfirm={reassignTo => {
            setOrganizations(current =>
              current.filter(org => org.id !== detaching.id)
            );
            setPendingDetach(current => [
              ...current,
              { id: detaching.id, reassignTo }
            ]);
            setDetaching(null);
          }}
        />
      )}
    </Modal>
  );
};

export default memo(EditAdminModal);
